import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { useSearchParams } from "react-router-dom";
import { analyticsService } from "../../services/AnalyticsService";
import axios from "axios";
import LogRocket from "logrocket";
import StepOne from "./StepOne/StepOne";
import StepTwo from "./StepTwo/StepTwo";
import { Container, Title, Link, LinksContainer, Paragraph } from "../../components/GeneralStyles/GeneralStyles";
import LeftArrow from "./Assets/Left Arrow.svg";
import { baseUrl, loginUrl } from "../../utils/env";
import { handleAction } from "./Signup.service";
import { useUnload } from "../../hooks/useUnload";
import Loader from "../LoadingScreen/LoadingScreen";
import { rollbarIdentifier, reportError } from "../../utils/rollbar";
import { useSignup } from "../../contexts/SignupContext";
import { determineUserType } from "../../utils/UserType";

const Signup = ({ template_name }: any) => {
  const {
    username,
    password,
    given_name,
    family_name,
    fullNumber,
    groupIds,
    UUID,
    recaptchaToken,
    companyName,
    setCognitoUser,
    setLoading,
    setEmailCandidate,
    setUUID,
    setUserName,
    setGroupIds,
    setAcademyId,
    setAllowedCountries,
    setCountryCode,
    error,
    phone_number,
    setFullNumber,
    country_code,
    setError,
    setUserExists,
    setPhoneExists,
    setCognitoError,
    setPassword,
    setGivenName,
    setFamilyName,
    setPhoneNumber,
    setRecaptchaToken,
    setCompanyName,
    setChecked,
    setDisclaimerError,
    setIsDisabled,
    checked,
    emailError,
    invalidPassword,
    setEmailError,
    loading,
  } = useSignup();
  const [searchParams, setSearchParams] = useSearchParams();
  const userType = determineUserType();
  type StepType = "stepOne" | "stepTwo";
  type StepsConfig = {
    [key in StepType]: {
      component: JSX.Element;
      nextStep?: string;
    };
  };

  // Define the flow for each user type
  const usersFlow: Record<typeof userType, any> = {
    candidate: {
      flowStartStep: "stepOne",
      authSignupCfg: {
        username,
        password,
        attributes: {
          given_name,
          family_name,
          phone_number: fullNumber,
          "custom:platform_data": JSON.stringify({ candidate_group_ids: groupIds }),
        },
        validationData: { recaptchaToken: recaptchaToken, candidateToken: UUID, refererDomain: window.location.hostname },
      },
      navigateOnAuthSignup: "/phoneconfirmation",
    },
    selfService: {
      flowStartStep: "stepTwo",
      authSignupCfg: {
        username,
        password,
        attributes: {
          given_name,
          family_name,
          "custom:platform_data": JSON.stringify({
            self_service: true,
            company_name: companyName,
            creation_info: JSON.stringify({ state: "running", profile: "Self-Service|ASR" }),
          }),
        },
        validationData: { recaptchaToken: recaptchaToken, refererDomain: window.location.hostname },
      },
      navigateOnAuthSignup: "/emailconfirmation",
    },
  };

  // Set the initial step based on the user type
  const [step, setStep] = useState<StepType>(usersFlow[userType].flowStartStep);

  let navigate = useNavigate();
  const { deregister } = useUnload(() => {});

  const queryParams = searchParams.get("token");
  const usernameParam = searchParams.get("username");

  // Resend confirmation code if usernameParam is present (This handles a case where user is back at the signup page before confirming their email)
  useEffect(() => {
    if (usernameParam) {
      const decodedUsername = decodeURIComponent(usernameParam);
      resendConfirmation(decodedUsername);
    }
  }, [usernameParam]);

  async function resendConfirmation(username: any) {
    try {
      await Auth.resendSignUp(username);
      console.log("confirmation code resent successfully");
      usernameParam &&
        setCognitoUser((prevState: any) => ({
          ...prevState,
          username: usernameParam,
        }));
      navigate(usersFlow[userType].navigateOnAuthSignup);
    } catch (err) {
      console.log("error resending confirmation code: ", err);
    }
  }

  // Get user data from candidate API
  useEffect(() => {
    if (!usernameParam) {
      setLoading(false);
    }
    if (queryParams) {
      setEmailCandidate(true);
      setUUID(queryParams);
      setLoading(false);

      let config = {
        method: "get",
        url: `${baseUrl}/api/v1/users/${queryParams}/candidate`,
      };

      axios(config)
        .then((res) => {
          setUserName(res.data.email);
          setGroupIds(res.data.geo_restricted_group_ids);
          setAcademyId(res.data.academy_id);
          setAllowedCountries(res.data.allowed_phone_country_codes);
          if (res.data.allowed_phone_country_codes?.length > 0) {
            setCountryCode(res.data.allowed_phone_country_codes[0]);
          }
          if (res.data.allowed_phone_country_codes.length == 1) {
            setCountryCode(res.data?.allowed_phone_country_codes[0]);
          }
          analyticsService.identify(username);
          analyticsService.setEmail(username);
          analyticsService.track("Signup - User arrived at sign-up page", { academy_id: res.data.academy_id }, () => {});
          LogRocket.identify(res.data.email, { name: res.data.email });
          rollbarIdentifier(res.data.email);
        })
        .catch((err) => {
          console.log(err.response?.status);
          reportError("Signup - Error in candidate API", error);
          if (err.response?.status === 401) {
            if (loginUrl) {
              window.location.href = loginUrl;
            } else {
              console.error("loginUrl is not defined.");
              // Optionally handle this case further e.g. display an error to the user
            }
          } else if (err.response?.status === 404) {
            navigate("/404");
          }
        });
    } else return;
  }, []);

  // Set full phone number
  useEffect(() => {
    // Remove leading zeros from phone_number
    const phoneNumberWithoutLeadingZeros = phone_number.replace(/^0+/, "");
    setFullNumber(country_code + phoneNumberWithoutLeadingZeros);
  }, [phone_number, country_code, fullNumber]);

  const handleStepChange = () => {
    setError(false);
    setUserExists(false);
    setPhoneExists(false);
    setCognitoError(null);
    if (step === "stepOne" && phone_number.length >= 7) {
      setStep("stepTwo");
      analyticsService.track("Signup - User clicked next step", {}, () => {});
    } else {
      analyticsService.track("Signup - User clicked back to previous step", {}, () => {});
      setStep("stepOne");
    }
  };

  const handleChange = (action: any, e: any) => {
    handleAction(action, e, setPassword, setUserName, setGivenName, setFamilyName, setPhoneNumber, setCountryCode, setRecaptchaToken, setCompanyName);
  };

  const handleChecked = (e: any) => {
    setChecked(e.currentTarget.checked);
    setDisclaimerError(false);
  };

  const onSubmit = (event: any) => {
    event.preventDefault();
    deregister();
    if (userType === "selfService") {
      analyticsService.track("SES - Signup Button Clicked", {}, () => {});
      analyticsService.identify(username);
      analyticsService.setEmail(username);
      rollbarIdentifier(username);
      LogRocket.identify(username, { name: username });
    }
    setIsDisabled(true);
    async function signUp() {
      try {
        const token = await recaptchaRef.current.executeAsync();

        const userData = usersFlow[userType].authSignupCfg;
        if (template_name) {
          userData.clientMetadata = { template_name };
        }
        userData.validationData["recaptchaToken"] = token
        
        const { user } = await Auth.signUp(userData);
        setCognitoUser(user);
        navigate(usersFlow[userType].navigateOnAuthSignup);
      } catch (error) {
        const err = error as Error;
        setCognitoError(err);

        // Determine the level of reporting based on error type
        const reportLevel = ["UsernameExistsException", "NotAuthorizedException", "InvalidParameterException"].includes((error as any).code) ? "info" : "error";

        reportError("Signup process", error, reportLevel);

        setIsDisabled(false);
        if ((error as any).code === "UsernameExistsException" && usernameParam) {
          try {
            // Assuming usersFlow[userType].authSignupCfg has a username field
            await Auth.resendSignUp(usersFlow[userType].authSignupCfg.username);
            navigate(usersFlow[userType].navigateOnAuthSignup === "/emailconfirmation" ? "/emailconfirmation" : "/phoneconfirmation");
          } catch (resendError) {
            console.log("Error resending confirmation code:", resendError);
          }
        } else if ((error as any).message === "PreSignUp failed with error phone number already exists.") {
          setPhoneExists(true);
        } else {
          setError(true);
        }
      }
    }
    if (checked && !emailError && !invalidPassword) {
      signUp();
    } else if (emailError) {
      setEmailError(true);
    } else if (!checked) {
      setDisclaimerError(true);
    }
  };
  const recaptchaRef = React.createRef();

  const stepsCfg: StepsConfig = {
    stepOne: {
      component: <StepOne handleChange={handleChange} handleStepChange={handleStepChange} queryParams={queryParams} step={step} />,
      nextStep: "stepTwo",
    },
    stepTwo: {
      component: <StepTwo step={step} handleChange={handleChange} handleChecked={handleChecked} recaptchaRef={recaptchaRef} />,
    },
  };


  return loading ? (
    <Loader />
  ) : (
    <Container onSubmit={onSubmit}>
      <Title>Sign up</Title>
      {userType === "candidate" ? (
        <Paragraph
          id="signup-description"
          marginBottom={step === "stepOne" ? "32px" : "0px"}
          aria-label="Sign up with the same details used in your Paypal / Payoneer account.">
          For security reasons and future payments, make sure to sign up with the same details used in your Paypal / Payoneer account.
        </Paragraph>
      ) : null}
      {stepsCfg[step].component}
      <LinksContainer>
        <Link href={loginUrl} target="_blank" tabIndex={0} style={{ color: "#1A4AF0" }} aria-label="Go to login page" role="button">
          Go to login instead
        </Link>
        <Link
          href={
            userType === "candidate"
              ? "https://support.verbit.ai/hc/en-us/articles/360015514940-How-To-Contact-Verbit-Customer-Support"
              : "https://verbit.ai/resources/help-getting-started-with-verbit/"
          }
          target="_blank"
          id="SupportLink"
          tabIndex={0}
          style={{ color: "#1A4AF0" }}
          aria-label="Contact Support"
          role="button">
          Contact Support
        </Link>
      </LinksContainer>
      {step === "stepTwo" && userType === "candidate" ? (
        <Link className="back-button" onClick={handleStepChange} width={"55px"} tabIndex={1} aria-label="Go back to the previous step" role="button">
          <img src={LeftArrow} alt="Back to the previous step" />
          Back
        </Link>
      ) : null}
    </Container>
  );
};

export default Signup;
